import { clone } from "lodash";
import React, { createContext, Dispatch, ReactNode, useContext, useReducer } from "react";
import invariant from "tiny-invariant";
import { getADefaultCalendarColor } from "./calendar-coloring";
import {
  CalendarColorsAction,
  calendarColorsReducer,
  CalendarColorsState,
  CalendarId,
} from "./calendarColorsReducer";

const StateContext = createContext<CalendarColorsState>({});
const DispatchContext = createContext<Dispatch<CalendarColorsAction> | undefined>(undefined);

type ProviderProps = {
  children: ReactNode;
  initValue?: CalendarColorsState;
};

export const CalendarColorsProvider = ({ children, initValue = {} }: ProviderProps) => {
  const [state, dispatch] = useReducer(calendarColorsReducer, initValue);

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </DispatchContext.Provider>
  );
};

export const useReadCalendarColor = (calendarId?: CalendarId) => {
  const context = useContext(StateContext);

  if (!calendarId) {
    return undefined;
  }

  return context[calendarId];
};

export const useReadCalendarColors = () => {
  const context = useContext(StateContext);
  return clone(context);
};

/**
 * Similar to `useReadCalendarColor` but sets a value if not found return value gauranteed
 *
 * @param calendarId
 * @returns a calendar color set
 */
export const useReadUpdateCalendarColor = (calendarId?: CalendarId) => {
  const readContext = useReadCalendarColors();
  const updateContext = useUpdateCalendarColors();
  const id = calendarId || "";

  let calendarColorSet = readContext[id];

  if (!calendarColorSet) {
    calendarColorSet = getADefaultCalendarColor(readContext);
    updateContext({
      type: "set-calendarColors",
      payload: { [id]: calendarColorSet },
    });
  }

  return calendarColorSet;
};

export const useUpdateCalendarColors = () => {
  const context = useContext(DispatchContext);
  invariant(context, "useUpdateCalendarColors must be within CalendarColorsProvider");
  return context;
};
